/**
 * Sound → per-tile field for the always-on sound overlay.
 *
 * This REPLACES the old expanding-ring / time-propagated wave model. The
 * region is the exact tile set the gameplay flood-fill produces (the same
 * `floodSound` that guards hear through), computed *instantly*: the full
 * region appears on the tick a sound is emitted — there is no radial
 * expansion, no wavefront tweening. What the player sees tinted is exactly
 * what a guard standing on that tile could hear.
 *
 *   - Spatial intensity falls off with the remaining sound budget (residual)
 *     at each tile → brightest at the source, faint at the audible edge.
 *   - A short per-event temporal envelope (CONFIG.sound.vizPersistTicks,
 *     scaled up for louder sounds) fades a one-shot sound out over ~0.3s so a
 *     single footstep doesn't strobe for one frame. This is an opacity fade
 *     of a *static* region, not a spatial animation.
 *   - Hue is an iridescent rainbow: it drifts across the region with distance
 *     from the source and shimmers slowly over time. Emitter identity does
 *     NOT pick the colour anymore — the field is one shared shine.
 *
 * The renderer does NOT draw a fill quad over the tile — it multiplies the
 * tile glyph's own colour toward `color` by `intensity`, and reveals the tile
 * through the fog, so the sound region is equally bright inside and outside
 * the vision cone.
 *
 * Floods are cached per event via WeakMap so we don't recompute every frame.
 *
 * No PixiJS here — this is pure data. See CLAUDE.md > Sound / Rendering.
 */

import { CONFIG } from '../config';
import { type MapData } from '../sim/map';
import { floodSound } from '../sim/sound';
import type { SoundEvent } from '../sim/soundQueue';

/** Per-tile sound contribution. */
export interface SoundFieldCell {
  /** 0..1 strength — how far to push the tile toward its multiplied hue.
   *  Tracks the remaining sound budget (bright at source, faint at edge). */
  intensity: number;
  /** Iridescent rainbow colour (0xRRGGBB) the tile's own colour is multiplied
   *  toward. Hue drifts with distance from the source + a slow time shimmer. */
  color: number;
}

/** Hue advance per tile of distance from the source — small so the rainbow
 *  reads as a gentle gradient across a room, not a hard banding. */
const HUE_PER_TILE = 0.035;
/** Saturation / value of the rainbow hue used for the multiply. Full value so
 *  multiplying a tile toward it shifts hue without crushing luminance more
 *  than the multiply itself already does. */
const HUE_SAT = 1;
const HUE_VAL = 1;

/** Cached flood per event. WeakMap so consumed / evicted events GC out. */
const floodCache = new WeakMap<SoundEvent, Map<number, number>>();

function getFlood(e: SoundEvent, map: MapData): Map<number, number> {
  let f = floodCache.get(e);
  if (!f) {
    f = floodSound(e.x, e.y, e.loudness, map);
    floodCache.set(e, f);
  }
  return f;
}

/** How long this event's overlay lingers (ticks). Louder = a touch longer. */
function persistTicks(loudness: number): number {
  return CONFIG.sound.vizPersistTicks + loudness;
}

/**
 * Build the sound field for the current frame.
 *
 * @param shimmer a slowly-advancing phase (seconds × rate) that rotates the
 *   whole rainbow over time for the "iridescent" shimmer. Pure cosmetic.
 */
export function computeSoundField(
  events: readonly SoundEvent[],
  map: MapData,
  shimmer: number,
  /** Player tile — only sounds the player can actually HEAR (whose flood
   *  reaches this tile with loudness left) are visualised. */
  playerX: number,
  playerY: number,
): Map<number, SoundFieldCell> {
  const out = new Map<number, SoundFieldCell>();
  const playerIdx = playerY * map.width + playerX;
  for (const e of events) {
    const persist = persistTicks(e.loudness);
    if (e.age >= persist) continue;
    // Temporal envelope: full at emission, linear fade to 0 at `persist`.
    const env = Math.max(0, 1 - e.age / persist);
    if (env <= 0) continue;
    const flood = getFlood(e, map);
    // Out of the player's hearing range → don't render it at all.
    if ((flood.get(playerIdx) ?? 0) <= 0) continue;
    for (const [idx, residual] of flood) {
      if (residual <= 0) continue;
      // Spatial falloff: residual is the loudness left at this tile.
      const spatial = Math.min(1, residual / e.loudness);
      const intensity = spatial * env;
      if (intensity <= 0.02) continue;
      // Distance from source (cost through the sound graph) drives the hue
      // gradient; shimmer rotates it over time. frac() keeps it in [0,1).
      const cost = e.loudness - residual;
      let hue = (cost * HUE_PER_TILE + shimmer) % 1;
      if (hue < 0) hue += 1;
      const prior = out.get(idx);
      if (!prior) {
        out.set(idx, { intensity, color: hsvToRgb(hue, HUE_SAT, HUE_VAL) });
      } else if (intensity > prior.intensity) {
        prior.intensity = intensity;
        prior.color = hsvToRgb(hue, HUE_SAT, HUE_VAL);
      }
    }
  }
  return out;
}

/** HSV (all 0..1) → 0xRRGGBB. */
export function hsvToRgb(h: number, s: number, v: number): number {
  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);
  let r: number, g: number, b: number;
  switch (i % 6) {
    case 0: r = v; g = t; b = p; break;
    case 1: r = q; g = v; b = p; break;
    case 2: r = p; g = v; b = t; break;
    case 3: r = p; g = q; b = v; break;
    case 4: r = t; g = p; b = v; break;
    default: r = v; g = p; b = q; break;
  }
  return (Math.round(r * 255) << 16) | (Math.round(g * 255) << 8) | Math.round(b * 255);
}
